/**
 * Chart.tsx - Chart - Universal Chart Component
 * 
 * CHANGE HISTORY:
 * - 251021_0438 [46df53c] danielbeer: feat: complete comprehensive package migration to full standards
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useMemo, useCallback } from 'react';
import { calculateGridLayout } from './layouts/GridLayout';
import { CHART_LAYOUTS } from './metadata/layoutMetadata';
import './Chart.css';

export const Chart = ({
  // Required
  data,
  layout = 'grid',
  
  // Layout options
  layoutOptions = {},
  
  // Node/Edge styling
  nodeType = 'default',
  edgeType = 'straight',
  
  // Interactions
  interactive = true,
  zoomable = false,
  collapsible = false,
  editable = false,
  
  // Event handlers
  onNodeClick,
  onEdgeClick,
  onLayoutComplete,
  
  // Styling
  style = {},
  className = '',
  
  ...otherProps
}) => {
  // Get layout metadata
  const layoutMeta = useMemo(() => {
    return CHART_LAYOUTS[layout] || CHART_LAYOUTS.grid;
  }, [layout]);
  
  // Calculate layout
  const layoutResult = useMemo(() => {
    // Prepare nodes and edges from data
    const { nodes, edges } = prepareData(data);
    
    // Apply layout algorithm
    switch (layout) {
      case 'grid':
        return calculateGridLayout(nodes, edges, {
          ...layoutMeta.defaultOptions,
          ...layoutOptions
        });
      
      case 'hierarchical':
      case 'force':
      case 'radial':
      case 'dagre':
      case 'sankey':
      case 'circular':
        // Placeholder - to be implemented
        console.warn(`Layout "${layout}" not yet implemented. Using grid layout.`);
        return calculateGridLayout(nodes, edges, {
          ...CHART_LAYOUTS.grid.defaultOptions,
          ...layoutOptions
        });
      
      default:
        console.warn(`Unknown layout "${layout}". Using grid layout.`);
        return calculateGridLayout(nodes, edges, {
          ...CHART_LAYOUTS.grid.defaultOptions,
          ...layoutOptions
        });
    }
  }, [data, layout, layoutOptions, layoutMeta]);
  
  // Handle node click
  const handleNodeClick = useCallback((node, event) => {
    if (onNodeClick) {
      onNodeClick(node, event);
    }
  }, [onNodeClick]);
  
  // Notify when layout is complete
  React.useEffect(() => {
    if (onLayoutComplete && layoutResult) {
      onLayoutComplete(layoutResult);
    }
  }, [layoutResult, onLayoutComplete]);
  
  // Render
  return (
    <div 
      className={`chart-container chart-layout-${layout} ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        ...style
      }}
      {...otherProps}
    >
      {/* Render nodes */}
      {layoutResult.nodes.map((node, index) => (
        <div
          key={node.id || index}
          className={`chart-node chart-node-${nodeType}`}
          style={{
            position: 'absolute',
            left: node.x,
            top: node.y,
            width: node.width,
            height: node.height,
            cursor: interactive ? 'pointer' : 'default'
          }}
          onClick={(e) => handleNodeClick(node, e)}
        >
          {renderNode(node, nodeType)}
        </div>
      ))}
      
      {/* Render edges (if any) */}
      {layoutResult.edges && layoutResult.edges.length > 0 && (
        <svg
          className="chart-edges"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          {layoutResult.edges.map((edge, index) => (
            <line
              key={edge.id || index}
              x1={edge.x1}
              y1={edge.y1}
              x2={edge.x2}
              y2={edge.y2}
              stroke="var(--color-border, var(--border-light))"
              strokeWidth={2}
            />
          ))}
        </svg>
      )}
    </div>
  );
};

/**
 * Prepare data for layout calculation
 * Converts various data formats to nodes/edges structure
 */
function prepareData(data) {
  // If already in nodes/edges format
  if (data && typeof data === 'object' && (data.nodes || data.edges)) {
    return {
      nodes: data.nodes || [],
      edges: data.edges || []
    };
  }
  
  // If array of items, treat as nodes
  if (Array.isArray(data)) {
    return {
      nodes: data.map((item, index) => ({
        id: item.id || index,
        ...item
      })),
      edges: []
    };
  }
  
  // Single item
  if (data && typeof data === 'object') {
    return {
      nodes: [{ id: 0, ...data }],
      edges: []
    };
  }
  
  // Empty
  return { nodes: [], edges: [] };
}

/**
 * Render node based on type
 */
function renderNode(node, nodeType) {
  switch (nodeType) {
    case 'person':
      return (
        <div className="chart-node-person">
          {node.photo && <img src={node.photo} alt={node.label} />}
          <div className="chart-node-label">{node.label || node.name}</div>
          {node.title && <div className="chart-node-title">{node.title}</div>}
        </div>
      );
    
    case 'process':
      return (
        <div className="chart-node-process">
          {node.icon && <span className="chart-node-icon">{node.icon}</span>}
          <div className="chart-node-label">{node.label}</div>
        </div>
      );
    
    case 'dot':
      return (
        <div className="chart-node-dot">
          <div className="chart-node-label">{node.label}</div>
        </div>
      );
    
    case 'default':
    default:
      return (
        <div className="chart-node-default">
          {node.label || node.name || node.id}
        </div>
      );
  }
}

export default Chart;
